<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed, onBeforeMount, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { IonApp } from "@ionic/vue";
import { PushNotifications } from "@capacitor/push-notifications";
import { NativeMarket } from "@capacitor-community/native-market";
import * as Sentry from "@sentry/capacitor";
import { SplashScreen } from "@capacitor/splash-screen";
import { Network } from "@capacitor/network";
import { App, type URLOpenListenerEvent } from "@capacitor/app";
import { AppTrackingTransparency } from "capacitor-plugin-app-tracking-transparency";

import { type Component } from "vue";

import initSentry from "@/Plugins/Sentry";
import AppRouter from "./Router";

import { APP_ROUTES } from "@/Generated/Routes/paths";
import { config } from "@/Common/envConfig";

import TcModal from "@/Components/UI/TcModal.vue";
import TcSnackbar from "@/Components/UI/TcSnackbar.vue";
import Privacy from "@/Components/Extend/TcPrivacy.vue";
import EventCapacitorManager from "@/Components/Extend/TcEventCapacitorManager.vue";
import RouterWrapper from "@/Router/RouterWrapper.vue";
import VideoCallVonageWrapper from "@/Modules/VideoCall/Components/WrapperVonage.vue";
import VideoCallRatingModal from "@/Modules/VideoCall/Components/RatingModal.vue";
import VideoCallConfig from "@/Modules/VideoCall/Components/VideoCallConfig.vue";

import useAppStore from "@/Modules/App/Store";
import useChatStore from "@/Modules/Chat/Store";
import useVideoCallStore from "@/Modules/VideoCall/Store";
import useAuthStore from "@/Modules/Auth/Store";
import useUserStore from "@/Modules/User/Store";
import { addTrackersAndCookiesBanner } from "@/Modules/App/TrackersAndCookies";
import { addGoogleGTM } from "@/Modules/App/TrackersAndCookies/GoogleGTM";

import { hasValue, isObject } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";
import { isAndroidApp, isApp, isIosApp, platformType, platforms } from "@/Utils/Ionic";
import { danger, log } from "@/Utils/Debug";
import { waitFor } from "@/Utils/Promises";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const appStore = useAppStore();
const authStore = useAuthStore();
const userStore = useUserStore();
const chatStore = useChatStore();
const videoCallStore = useVideoCallStore();

const height = ref<number>(0);
const width = ref<number>(0);
const showPreview = ref<boolean>(false);
const trackersInjected = ref<boolean>(false);
const PreviewViewComp = ref<Component | null>(null);

const updateViewport = () => {
  const newHeight = isApp() ? window.outerHeight : window.innerHeight;
  const newWidth = isApp() ? window.outerWidth : window.innerWidth;

  if (newHeight !== appStore.viewportHeight || newWidth !== appStore.viewportWidth) {
    height.value = newHeight;
    width.value = newWidth;

    appStore.updateViewport(newWidth, newHeight);

    document.body.style.setProperty("--app-height", `${newHeight}px`);
    document.getElementById("app")?.style.setProperty("height", `${newHeight}px`);
    document.body.style.setProperty("--app-width", `${newWidth}px`);
    document.getElementById("app")?.style.setProperty("width", `${newWidth}px`);
  }
};

const updateStatusOnline = () => {
  appStore.updateStatusOnline(true);
};

const snackbarParams = computed(() => {
  if (hasValue(appStore.snackbar)) {
    return appStore.snackbar;
  } else {
    return { message: "" };
  }
});

const hasVideoCallInProgress = computed(() => {
  return hasValue(userStore.user) && isValidString(videoCallStore.token);
});

const needRating = computed(() => {
  return (
    videoCallStore.isRatingRequired &&
    !hasValue(videoCallStore.nextCallOptions) &&
    isValidString(videoCallStore.roomId) &&
    !isValidString(videoCallStore.token)
  );
});

const updateStatusOffline = () => appStore.updateStatusOnline(false);

const handleUpdateApp = () => {
  if (isAndroidApp()) {
    NativeMarket.openStoreListing({ appId: "com.terapiachat.android" });
  } else if (isIosApp()) {
    NativeMarket.openStoreListing({ appId: "id1223472664" });
  }
};

const handleCloseUpdateApp = () => {
  if (!appStore.isForceUpdate) {
    appStore.needUpdate = false;
  }
};

const handleFinishPreview = () => {
  router.push({ name: APP_ROUTES.onboarding.root.name });

  waitFor(0.2).then(() => {
    showPreview.value = false;
  });
};

const handleGoToLogin = () => {
  router.push({ name: APP_ROUTES.login.root.name });

  showPreview.value = false;
};

const addTrackers = async () => {
  if (config.trackersDisabled) return;
  if (trackersInjected.value) return;

  if (
    !isApp() ||
    isAndroidApp() ||
    (await AppTrackingTransparency.getStatus().then(({ status }) => status === "authorized"))
  ) {
    log("ADDING TRACKERS");

    trackersInjected.value = true;
    addGoogleGTM();
    try {
      initSentry(getCurrentInstance()!.appContext.app, AppRouter.getInstance());

      const scope = Sentry.getCurrentScope();

      scope.clear();

      if (isIosApp()) {
        scope.setTag("ios_version", config.iosAppVersion);
      } else if (isAndroidApp()) {
        scope.setTag("android_version", config.androidAppVersion);
      } else {
        scope.setTag("web_version", config.webVersion);
      }

      scope.setTag("device_type", platformType());
      scope.setExtra("platforms", platforms().join(","));

      if (isValidString(config.sentryEnv)) {
        scope.setTag("environment", config.sentryEnv);
      }
    } catch (error) {
      log("SENTRY ERROR", error);
    }
  }
};

const handleCompleteRating = () => {
  videoCallStore.cleanRaiting();
};

const handleCloseRating = () => {
  videoCallStore.cleanRaiting();
};

onBeforeMount(async () => {
  if (process.env.IONIC) {
    const Module = await import("@/Views/preview/index.vue");

    PreviewViewComp.value = Module.default;
  }

  if (isApp()) {
    showPreview.value = appStore.isNeededPreview();
  } else {
    showPreview.value = false;
  }
});

onMounted(async () => {
  if (process.env.IONIC) {
    const Module = await import("@/Views/preview/index.vue");

    PreviewViewComp.value = Module.default;
  }

  if (isApp()) {
    SplashScreen.hide();
  }

  appStore.checkUpdate();

  window.addEventListener("online", updateStatusOnline);
  window.addEventListener("offline", updateStatusOffline);
  window.addEventListener("resize", updateViewport);

  updateViewport();
  addTrackers();

  if (isApp()) {
    try {
      Network.addListener("networkStatusChange", (status) => {
        if (route.name === APP_ROUTES.onboarding.root.name) {
          log(`Onboarding change network is connected ${status.connected}`);
          return;
        } else if (status.connected && route.name === APP_ROUTES.error.offline.root.name) {
          router.push({ name: APP_ROUTES.chat.root.name });
        } else if (!status.connected && !hasVideoCallInProgress.value) {
          appStore.clearStores(false);

          router.push({ name: APP_ROUTES.error.offline.root.name });
        }
      });

      if (isApp()) {
        App.addListener("appStateChange", async ({ isActive }) => {
          if (isActive) {
            addTrackers();
            PushNotifications.removeAllDeliveredNotifications();
            appStore.checkUpdate();
          }
        });
      }

      App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
        log("appUrlOpen listener");

        if (isObject(event) && isValidString(event.url)) {
          log(`Open with url ${event.url}`);

          // Example url: https://beerswift.app/tabs/tabs2
          // path = /tabs/tabs2
          const path = event.url.replace(new RegExp("https:[/]{2}[^/]+", "g"), "");
          const [pathname, queryString] = path.split("?");

          if (pathname === "/auth") {
            const query = new URLSearchParams(queryString);

            router.push({
              name: APP_ROUTES.sso.root.name,
              query: { userToken: query.get("userToken") },
            });
          } else {
            if (authStore.isUserLogged) {
              router.push({ name: APP_ROUTES.home.root.name });
            } else {
              router.push({ name: APP_ROUTES.login.root.name });
            }
          }
        }
      });

      const status = await Network.getStatus();

      if (!status.connected) {
        router.push({ name: APP_ROUTES.error.offline.root.name });
      }
    } catch (e) {
      danger("ERROR Network PLUGIN");
    }
  } else {
    addTrackersAndCookiesBanner();
  }
});

onUnmounted(() => {
  chatStore.closeWebSocket();

  window.removeEventListener("online", updateStatusOnline);
  window.removeEventListener("offline", updateStatusOffline);
  window.removeEventListener("resize", updateViewport);

  if (isApp()) {
    App.removeAllListeners();

    try {
      Network.removeAllListeners();
    } catch (e) {
      danger(e);
    }
  }
});
</script>

<template>
  <RouterWrapper>
    <EventCapacitorManager>
      <IonApp
        class="ionic_app"
        :class="{ 'ionic_app--ios': isIosApp(), 'ionic_app--android': isAndroidApp() }"
      >
        <div style="position: absolute; top: -1000px">
          <!-- svg gradient for loaders -->
          <svg width="0" height="0">
            <defs>
              <linearGradient
                id="cl1"
                gradientUnits="userSpaceOnUse"
                x1="0"
                y1="0"
                x2="1"
                y2="1"
                gradientTransform="translate(17.8333 19.0007) rotate(-26.565) scale(20.87 18.6766)"
              >
                <stop stop-color="#50ABED" />
                <stop offset="0.811989" stop-color="#50ABED" stop-opacity="0" />
                <stop offset="1" stop-color="#50ABED" stop-opacity="0" />
              </linearGradient>
            </defs>
          </svg>
          <!-- svg gradient for loaders -->
        </div>

        <Privacy v-if="!showPreview">
          <TcSnackbar
            :message="snackbarParams.message"
            :messageParameters="snackbarParams.messageParameters"
            :type="snackbarParams.type"
          />

          <VideoCallVonageWrapper
            v-if="hasVideoCallInProgress"
            :isHidden="videoCallStore.isHiddenVideoCall"
            @onShowChat="videoCallStore.toggleHideVideoCall"
          />

          <VideoCallRatingModal
            v-if="needRating"
            @onComplete="handleCompleteRating"
            @onClose="handleCloseRating"
          />
          <VideoCallConfig
            v-if="videoCallStore.showConfig"
            class="video_call_config"
            :class="{ ios_grid_app: isIosApp(), android_grid_app: isAndroidApp() }"
          />

          <TcModal
            v-if="
              appStore.needUpdate &&
              hasValue(appStore.versionRequirements) &&
              hasValue(appStore.versionRequirements.current)
            "
            :acceptButtonText="t('general.force_update.button')"
            :title="t('general.force_update.title', { version: appStore.versionRequirements.current })"
            :textContent="t('general.force_update.description')"
            @acceptButtonClicked="handleUpdateApp"
            @clickOutside="handleCloseUpdateApp"
          />

          <RouterView />
        </Privacy>

        <component
          :is="PreviewViewComp"
          class="preview_view"
          :class="{ ios_grid_app: isIosApp(), android_grid_app: isAndroidApp() }"
          v-else
          @onFinish="handleFinishPreview"
          @onLogin="handleGoToLogin"
        />
      </IonApp>
    </EventCapacitorManager>
  </RouterWrapper>
</template>

<style lang="scss">
:root {
  --app-height: 100%;
}

div#app {
  font-size: 16px;
  overflow-y: hidden;
  position: relative;
  width: 100%;
  height: 100vh;
  height: var(--app-height);
  max-height: var(--app-height);

  position: fixed;
  top: 0;
  left: 0;
}

body {
  min-height: var(--app-height);
}

.ionic_app {
  z-index: 2;
}

.preview_view {
  height: 100vh;
}

.video_call_config {
  position: absolute;
  height: 100vh;
  left: $size-navigation-width;
  top: 0;
  width: calc(100vw - $size-navigation-width);
  z-index: 10000000;
  overflow-y: scroll;

  @media (max-width: $breakpoint-m) {
    width: 100vw;
    left: 0;
  }
}
</style>
